import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { Package, Minus, Loader2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

interface Material {
  id: string;
  name: string;
  code: string | null;
  quantity: number;
  unit: string;
  category: string | null;
}

interface MaterialConsumptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderId?: string;
  workOrderNumber?: string;
  processRecordId?: string;
  onConsumed?: () => void;
}

export function MaterialConsumptionDialog({
  open,
  onOpenChange,
  workOrderId,
  workOrderNumber,
  processRecordId,
  onConsumed,
}: MaterialConsumptionDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [reason, setReason] = useState<string>('refuerzo');
  const [notes, setNotes] = useState<string>('');

  const selectedMaterial = materials.find(m => m.id === selectedMaterialId);

  useEffect(() => {
    if (open) {
      fetchMaterials();
    }
  }, [open]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const { data, error } = await api
        .from('raw_materials')
        .select('id, name, code, quantity, unit, category')
        .eq('is_active', true)
        .gt('quantity', 0)
        .order('name');

      if (error) throw error;
      setMaterials(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los materiales',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMaterialId || !quantity || parseFloat(quantity) <= 0) {
      toast({
        title: 'Error',
        description: 'Selecciona un material y cantidad válida',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await api.rpc('consume_material', {
        p_material_id: selectedMaterialId,
        p_quantity: parseFloat(quantity),
        p_reason: reason === 'refuerzo' ? 'Corte manual - Refuerzo' :
                  reason === 'reparacion' ? 'Corte manual - Reparación' :
                  reason === 'desperdicio' ? 'Desperdicio/Scrap' : 'Consumo manual',
        p_work_order_id: workOrderId || null,
        p_process_record_id: processRecordId || null,
        p_operator_id: user?.id || null,
        p_notes: notes || null,
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: 'Material consumido',
          description: `${data.quantity_consumed} ${data.unit} de ${data.material_name}. Stock restante: ${data.new_stock}`,
        });

        // Reset form
        setSelectedMaterialId('');
        setQuantity('');
        setNotes('');

        if (onConsumed) onConsumed();
        onOpenChange(false);
      } else {
        throw new Error(data?.error || 'Error desconocido');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo registrar el consumo',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const quantityNum = parseFloat(quantity) || 0;
  const isInsufficientStock = selectedMaterial && quantityNum > selectedMaterial.quantity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Consumir Material
          </DialogTitle>
          <DialogDescription>
            Registra el consumo manual de material para refuerzos, reparaciones, etc.
            {workOrderNumber && (
              <Badge variant="outline" className="ml-2">{workOrderNumber}</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Material selector */}
          <div className="space-y-2">
            <Label>Material *</Label>
            <Select value={selectedMaterialId} onValueChange={setSelectedMaterialId}>
              <SelectTrigger>
                <SelectValue placeholder={loading ? "Cargando..." : "Seleccionar material"} />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {materials.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex items-center justify-between w-full gap-2">
                      <span className="truncate">{m.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {m.quantity} {m.unit}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMaterial && (
              <p className="text-xs text-muted-foreground">
                Stock disponible: <strong>{selectedMaterial.quantity} {selectedMaterial.unit}</strong>
                {selectedMaterial.code && ` | Código: ${selectedMaterial.code}`}
              </p>
            )}
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label>Cantidad *</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0.00"
                className={isInsufficientStock ? 'border-red-500' : ''}
              />
              <div className="flex items-center px-3 bg-muted rounded-md text-sm">
                {selectedMaterial?.unit || 'm²'}
              </div>
            </div>
            {isInsufficientStock && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Stock insuficiente
              </p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Motivo</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="refuerzo">Refuerzo / Capa adicional</SelectItem>
                <SelectItem value="reparacion">Reparación</SelectItem>
                <SelectItem value="desperdicio">Desperdicio / Scrap</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Detalles adicionales..."
              rows={2}
            />
          </div>

          {/* Date display */}
          <div className="text-xs text-muted-foreground">
            Fecha: {format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedMaterialId || !quantity || isInsufficientStock}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <Minus className="w-4 h-4 mr-2" />
                Consumir Material
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
